import { useEffect } from 'react'
import { useNavigate, useOutletContext, useParams } from 'react-router-dom'

import { Box, Loading } from '@factorialco/deprecated-design-system'
import {
  EditSessionSidePanelInstructorsDocument,
  EditSessionSidePanelInstructorsQuery,
  EditSessionSidePanelQueryDocument,
  EditSessionSidePanelQueryQuery,
} from 'generated/resources/graphql'

import Sidepanel, { type Option } from 'modules/core/components/Sidepanel'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { checkFeatures } from 'modules/core/hooks/useFeatures'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { dateBackendDateTimeToUI } from 'modules/core/lib/dates'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { EmployeeForSelectFragmentNoAccess } from 'modules/core/components/EntitySelectWrapper/Entities/Employees/types'
import { EventBox } from 'modules/trainings/components/Revamp/NewClassWizard/EventDataStep/EventBox'
import { SessionData } from 'modules/trainings/components/Revamp/NewClassWizard/hooks/useNewClassWizardSteps'
import { formatDueDate } from 'modules/trainings/components/Revamp/TrainingsDetail/Classes/EditClass/ClassEvents'
import { useUpdateSession } from 'modules/trainings/hooks/Sessions/useUpdateSession'
import { useIsRelatorioEnabled } from 'modules/trainings/hooks/Settings/settingUtils'

export type SessionsTabOutletContext = {
  id: string
  classId: string
  canEdit: boolean
  trainingId: string | undefined
  classStartDate: string | undefined | null
  classEndDate: string | undefined | null
  sessionCount: number
}

type SessionNode = NonNullable<EditSessionSidePanelQueryQuery['trainings']['session']>
type InstructorNode =
  EditSessionSidePanelInstructorsQuery['trainings']['sessionInstructorsConnection']['nodes'][number]

/** Convert instructors from the connection shape to EmployeeForSelectFragmentNoAccess[] */
const transformInstructors = (instructors: InstructorNode[]): EmployeeForSelectFragmentNoAccess[] =>
  instructors.map((instructor) => ({
    id: instructor.employee.id,
    firstName: instructor.employee.firstName,
    fullName: instructor.employee.fullName,
    lastName: instructor.employee.lastName,
    managerId: instructor.employee.managerId,
    preferredName: instructor.employee.preferredName,
    avatar: instructor.employee.avatar,
    access: { avatar: instructor.employee.avatar?.url || null },
  }))

/** Build SessionData from the dedicated session query result. */
const sessionDataFromNode = (session: SessionNode, instructors: InstructorNode[]): SessionData => {
  const sendCalendarInvites = session.calendarInvite != null ? !!session.calendarInvite.sent : false

  return {
    ...session,
    startsAtHour: '',
    endsAtHour: '',
    recurrent: !!session.recurrent,
    frequency: session.frequency ?? undefined,
    reminders: [],
    duration: session.duration ?? '',
    link: session.link ?? '',
    description: session.description ?? '',
    location: session.location ?? '',
    startDate: session.startsAt ? dateBackendDateTimeToUI(session.startsAt) : '',
    sessionRelatorioUnicoField: {
      trainingSchedule: session.sessionRelatorioUnicoField?.trainingSchedule ?? '',
    },
    modality: session.modality ?? 'mixed',
    dueDate: session.dueDate ?? '',
    schedule: session.schedule,
    sessionInstructors: {
      employeeSelection: {
        type: 'manual',
        ids: instructors
          .map((instructor) => instructor.employee.id)
          .filter((id): id is string => id !== undefined && id !== null),
      },
      employeesData: transformInstructors(instructors),
      sessionId: session.id?.toString() ?? undefined,
    },
    sendCalendarInvites,
  }
}

const EditSessionSidepanel = () => {
  const { id, classId, canEdit, trainingId, classStartDate, classEndDate, sessionCount } =
    useOutletContext<SessionsTabOutletContext>()
  const { eventId } = useParams()
  const { company } = useCurrentSession()

  const { data, loading } = useQuery(EditSessionSidePanelQueryDocument, {
    variables: { eventId: eventId ?? '', companyId: company.id, withFF: true },
  })

  const { data: instructorsData, loading: instructorsLoading } = useQuery(
    EditSessionSidePanelInstructorsDocument,
    { variables: { sessionId: eventId ?? '' } }
  )

  const session = data?.trainings.session ?? undefined
  const instructorNodes = instructorsData?.trainings.sessionInstructorsConnection.nodes ?? []
  const featureNodes = data?.features?.featuresConnection.nodes
  const { has_dev_trainings: devTrainings } = checkFeatures(['dev_trainings'], featureNodes)
  const relatorioEnabled = useIsRelatorioEnabled(company.id)
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const back = () =>
    navigate(
      navigation.trainings.list.details_revamp.view.trainingClasses.detail.sessions.fullPath({
        id: id,
        classId: classId,
      })
    )

  const sessionDataToEdit = session && sessionDataFromNode(session, instructorNodes)

  const { update } = useUpdateSession()

  useEffect(() => {
    if (!loading && !session) {
      back()
    }
  }, [loading, session])

  const updateSession = async (sessionToUpdate: SessionData) => {
    track(trackingEvents.training.trainingSession.editSessionSave)
    const dueDate = formatDueDate(sessionToUpdate.dueDate)

    // Build session relatorio if enabled and there's a training schedule value
    const sessionRelatorio =
      relatorioEnabled && sessionToUpdate.sessionRelatorioUnicoField?.trainingSchedule !== undefined
        ? { trainingSchedule: sessionToUpdate.sessionRelatorioUnicoField.trainingSchedule }
        : undefined

    // Build session instructors with employee selection
    const sessionInstructors = {
      sessionId: String(sessionToUpdate.id),
      companyId: company.id,
      employeeSelection: sessionToUpdate.sessionInstructors.employeeSelection,
    }

    const response = await update({
      ...sessionToUpdate,
      id: sessionToUpdate.id ?? '',
      subsidized: sessionToUpdate.subsidized ?? false,
      dueDate: dueDate ?? '',
      recurrent: sessionToUpdate.recurrent,
      sessionRelatorio,
      sessionInstructors,
    })
    const handled = handleResult(response, () => {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('trainings.trainings_revamp.classes_sidepanel.events.error_update'),
      })
    })

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('trainings.trainings_revamp.classes_sidepanel.events.success_update'),
      })

      back()
    }
  }
  const optionsSidepanel: Option[] = [
    {
      danger: true,
      icon: 'delete',
      title: i18n.t('trainings.trainings_revamp.event_sidepanel.dropdown.delete'),
      to: navigation.trainings.list.details_revamp.view.trainingClasses.tab.event.view.deleteEvent.fullPath(
        {
          id: id ?? '',
          classId: classId,
          eventId: eventId ?? '',
        }
      ),
    },
  ]

  if (loading || instructorsLoading) return <Loading />
  if (!session) return null
  return (
    <Sidepanel
      width="wideXL"
      open
      title={session.name}
      onClose={back}
      options={sessionCount > 1 ? optionsSidepanel : undefined}
    >
      <Box gap="s24">
        <EventBox
          trainingId={trainingId ?? ''}
          trainingClassId={classId ?? ''}
          initialData={sessionDataToEdit}
          onClose={back}
          onSave={(s: SessionData) => updateSession(s)}
          groupStart={classStartDate ?? ''}
          groupEnd={classEndDate ?? ''}
          featureFlag={!!devTrainings}
        />
      </Box>
    </Sidepanel>
  )
}

export default EditSessionSidepanel
